import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance, getAiRequestErrorMessage, sendKeepaliveRequest } from "@/lib/api";
import { queue } from "@/components/Toast";
import type { ReasoningEffort } from "@/types/chat.type";
import type { WorkshopPerson, WorkshopSession } from "@/types/workshop.type";
import { workshopSessionsKey } from "./workshopKeys";

export function useWorkshopSessions(projectId: string | null) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: workshopSessionsKey(projectId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ sessions: WorkshopSession[] }>(
        `/workshop/projects/${projectId}/sessions`,
      );
      return res.data.sessions;
    },
    enabled: !!projectId,
  });

  const invalidateSessions = useCallback(() => {
    void qc.invalidateQueries({ queryKey: workshopSessionsKey(projectId) });
  }, [qc, projectId]);

  const createSession = useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post<{ session: WorkshopSession }>(
        `/workshop/projects/${projectId}/sessions`,
      );
      return res.data.session;
    },
    onSuccess: invalidateSessions,
  });

  /** Persists the session's picker (model "auto" | id, effort), title, and/or active skills. */
  const updateSession = useMutation({
    mutationFn: async (vars: {
      sessionId: string;
      title?: string;
      model?: string | null;
      reasoningEffort?: ReasoningEffort | null;
      /** Full-set replace; the chip row only ever removes a key. */
      enabledSkills?: string[];
    }) => {
      const { sessionId, ...body } = vars;
      await axiosInstance.put(`/workshop/sessions/${sessionId}`, body);
    },
    onSuccess: invalidateSessions,
    // A silent failure here reads as "the workshop forgot my model" on the
    // next open — the pick survives locally but never reached the session row.
    onError: (error) => {
      queue.add({
        title: "Couldn't save chat settings",
        description: getAiRequestErrorMessage(error),
        variant: "warning",
      });
    },
  });

  /** The OP renames a thread: shown at once, put back if the API refuses. */
  const renameSession = useMutation({
    mutationFn: async (vars: { sessionId: string; title: string }) => {
      await axiosInstance.put(`/workshop/sessions/${vars.sessionId}`, { title: vars.title });
    },
    onMutate: async ({ sessionId, title }) => {
      const key = workshopSessionsKey(projectId);
      await qc.cancelQueries({ queryKey: key });
      const before = qc.getQueryData<WorkshopSession[]>(key)?.find((s) => s.id === sessionId);
      qc.setQueryData<WorkshopSession[]>(key, (list) =>
        list?.map((s) => (s.id === sessionId ? { ...s, title } : s)),
      );
      return { before };
    },
    onError: (error, { sessionId }, context) => {
      const before = context?.before;
      if (before) {
        qc.setQueryData<WorkshopSession[]>(workshopSessionsKey(projectId), (list) =>
          list?.map((s) => (s.id === sessionId ? { ...s, title: before.title } : s)),
        );
      }
      queue.add({
        title: "Couldn't rename the chat",
        description: getAiRequestErrorMessage(error),
        variant: "warning",
      });
    },
    onSettled: invalidateSessions,
  });

  const deleteMutationKey = ["workshopDeleteSession", projectId];
  const deleteSession = useMutation({
    mutationKey: deleteMutationKey,
    mutationFn: async (sessionId: string) => {
      await axiosInstance.delete(`/workshop/sessions/${sessionId}`);
    },
    onMutate: async (sessionId) => {
      const key = workshopSessionsKey(projectId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<WorkshopSession[]>(key);
      const index = previous?.findIndex((s) => s.id === sessionId) ?? -1;
      const removed = index >= 0 ? previous![index] : null;
      qc.setQueryData<WorkshopSession[]>(key, (list) =>
        list?.filter((s) => s.id !== sessionId),
      );
      return { removed, index };
    },
    // Put back only the thread that failed, where it was — the list may have
    // changed since (a new thread, another delete).
    onError: (error, _sessionId, context) => {
      const removed = context?.removed;
      if (removed) {
        qc.setQueryData<WorkshopSession[]>(workshopSessionsKey(projectId), (list = []) => {
          if (list.some((s) => s.id === removed.id)) return list;
          const next = [...list];
          next.splice(Math.min(context.index, next.length), 0, removed);
          return next;
        });
      }
      queue.add({
        title: "Couldn't delete the chat",
        description: getAiRequestErrorMessage(error),
        variant: "warning",
      });
    },
    // Refetching while another delete is in flight would bring its row back.
    onSettled: () => {
      if (qc.isMutating({ mutationKey: deleteMutationKey }) > 1) return;
      invalidateSessions();
    },
  });

  /**
   * The OP shares the thread with people from the project. The row shows its
   * new people at once; the first share moves it onto the shared engine only
   * when the server confirms (`sharedAt` comes from the response), so no send
   * goes to the shared endpoint before the thread is shared.
   */
  const shareThread = useMutation({
    mutationFn: async (vars: { sessionId: string; people: WorkshopPerson[] }) => {
      const res = await axiosInstance.post<{ session: WorkshopSession }>(
        `/workshop/sessions/${vars.sessionId}/participants`,
        { userIds: vars.people.map((p) => p.userId) },
      );
      return res.data.session;
    },
    onMutate: async ({ sessionId, people }) => {
      const key = workshopSessionsKey(projectId);
      await qc.cancelQueries({ queryKey: key });
      const before = qc.getQueryData<WorkshopSession[]>(key)?.find((s) => s.id === sessionId);
      qc.setQueryData<WorkshopSession[]>(key, (list) =>
        list?.map((s) => {
          if (s.id !== sessionId) return s;
          const current = s.participants ?? [];
          const added = people.filter((p) => !current.some((c) => c.userId === p.userId));
          return {
            ...s,
            participants: [...current, ...added],
            participantIds: [...(s.participantIds ?? []), ...added.map((p) => p.userId)],
          };
        }),
      );
      return { before };
    },
    onError: (error, { sessionId }, context) => {
      const before = context?.before;
      if (before) {
        qc.setQueryData<WorkshopSession[]>(workshopSessionsKey(projectId), (list) =>
          list?.map((s) => (s.id === sessionId ? before : s)),
        );
      }
      queue.add({
        title: "Couldn't share the chat",
        description: getAiRequestErrorMessage(error),
        variant: "warning",
      });
    },
    onSuccess: (session) => {
      qc.setQueryData<WorkshopSession[]>(workshopSessionsKey(projectId), (list) =>
        list?.map((s) => (s.id === session.id ? { ...s, ...session } : s)),
      );
    },
    onSettled: invalidateSessions,
  });

  /**
   * Takes someone out of a shared thread: the OP removing a participant, or
   * a participant leaving (their own id) — then the thread leaves their list.
   */
  const removeParticipant = useMutation({
    mutationFn: async (vars: { sessionId: string; userId: string; leaving: boolean }) => {
      await axiosInstance.delete(`/workshop/sessions/${vars.sessionId}/participants/${vars.userId}`);
    },
    onMutate: async ({ sessionId, userId, leaving }) => {
      const key = workshopSessionsKey(projectId);
      await qc.cancelQueries({ queryKey: key });
      const list = qc.getQueryData<WorkshopSession[]>(key);
      const index = list?.findIndex((s) => s.id === sessionId) ?? -1;
      const before = index >= 0 ? list![index] : null;
      qc.setQueryData<WorkshopSession[]>(key, (current) =>
        leaving
          ? current?.filter((s) => s.id !== sessionId)
          : current?.map((s) =>
              s.id === sessionId
                ? {
                    ...s,
                    participants: s.participants?.filter((p) => p.userId !== userId),
                    participantIds: s.participantIds?.filter((id) => id !== userId),
                  }
                : s,
            ),
      );
      return { before, index };
    },
    onError: (error, { sessionId, leaving }, context) => {
      const before = context?.before;
      if (before) {
        qc.setQueryData<WorkshopSession[]>(workshopSessionsKey(projectId), (list = []) => {
          if (leaving) {
            if (list.some((s) => s.id === sessionId)) return list;
            const next = [...list];
            next.splice(Math.min(context.index, next.length), 0, before);
            return next;
          }
          return list.map((s) => (s.id === sessionId ? before : s));
        });
      }
      queue.add({
        title: leaving ? "Couldn't leave the chat" : "Couldn't remove them from the chat",
        description: getAiRequestErrorMessage(error),
        variant: "warning",
      });
    },
    onSettled: invalidateSessions,
  });

  /**
   * Leave a shared thread while the page is going away (a held leave whose
   * Undo ran out as the tab closed). The mutation above could be cut off with
   * the page, so this sends a keepalive request and drops the row from the
   * cache itself; the next read reconciles either way.
   */
  const leaveOnUnload = useCallback(
    (sessionId: string, userId: string) => {
      qc.setQueryData<WorkshopSession[]>(workshopSessionsKey(projectId), (list) =>
        list?.filter((s) => s.id !== sessionId),
      );
      sendKeepaliveRequest("DELETE", `/workshop/sessions/${sessionId}/participants/${userId}`);
    },
    [qc, projectId],
  );

  const compactSession = useMutation({
    mutationFn: async (sessionId: string) => {
      const res = await axiosInstance.post<{
        summary: string;
        compactedAtMessageId: string;
      }>(`/workshop/sessions/${sessionId}/compact`);
      return res.data;
    },
    onSuccess: invalidateSessions,
  });

  return {
    sessions: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    createSession,
    updateSession,
    renameSession,
    deleteSession,
    shareThread,
    removeParticipant,
    leaveOnUnload,
    compactSession,
    invalidateSessions,
  };
}
